import React from 'react';
import { Calendar, User, ArrowLeft, Tag } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Blog posts data - same as BlogPage but with full content
  const blogPosts = [
    {
      id: 1,
      title: 'Remembering God\'s Word: Lessons from Deuteronomy 6',
      excerpt: 'In a world saturated with distractions, the call to remember God\'s Word is more urgent than ever. Explore how biblical memory practices intersect with cognitive science, offering practical steps for spiritual growth.',
      content: `
        <div class="prose max-w-none">
          <p>In a world saturated with distractions, the call to remember God's Word is more urgent than ever. Our attention spans have shortened, yet Scripture insists that spiritual maturity depends on meditating on and internalizing God's truth. Deuteronomy 6:4–9, known as the Shema, serves as a theological cornerstone for this call to memory. It charges the people of God to love Him fully and to engrave His words on their hearts, teaching them diligently to their children and speaking of them in every setting.</p>
          
          <p>This command to remember is not only ancient but also strikingly modern. In an age of information overload, forgetfulness has become a spiritual epidemic. We retain headlines but often lose sight of holiness. As theologian James K.A. Smith notes, we are not just thinking beings, but desiring creatures whose habits shape what we love. To remember God's Word is not to merely recall verses, but to rehearse and reorient our affections toward God daily.</p>
          
          <h3>Historical and Cultural Context of Deuteronomy 6</h3>
          <p>The Shema was given at a pivotal moment in Israel's history, as the people prepared to enter the Promised Land. Moses emphasized the importance of not forgetting the Lord amid prosperity. The repeated injunctions to remember, teach, and inscribe the law reveal the covenantal nature of this memory—not merely intellectual, but relational and communal.</p>
          
          <p>Ancient Israel functioned as an oral culture, and memory was a key spiritual practice. Monuments, festivals, and rituals were not ornamental but mnemonic. Moses' emphasis on memory was a defense against spiritual amnesia, especially in the context of Canaanite pluralism.</p>
          
          <h3>Biblical Memory and Meditation</h3>
          <p>Biblical memory isn't nostalgia—it's redemptive recall. Israel's identity hinged on remembering God's deliverance (Exod. 13:3). The Lord's Supper, too, is an act of sacred remembering (Luke 22:19). Forgetting God, conversely, is tantamount to rebellion (Judg. 3:7).</p>
          
          <p>Meditation is not passive; it's active engagement, as described in Psalm 119:15: "I will meditate on your precepts and fix my eyes on your ways." Psalm 1:2–3 describes the blessed individual as one who delights in the law of the Lord and meditates on it day and night.</p>
          
          <h3>Practical Application</h3>
          <p>Take a moment to reflect:</p>
          <ul>
            <li>Do I have regular rhythms of reviewing God's Word?</li>
            <li>How do I ensure that I remember God's promises in daily stress or success?</li>
            <li>What tools could I adopt to improve scriptural recall?</li>
          </ul>
          
          <p>In prayer today, ask the Holy Spirit to bring to remembrance the truths you've learned. Consider scheduling weekly time to review verses you've highlighted or journal entries you've written. Repetition is not a weakness of faith—it's the rhythm of it.</p>
          
          <h3>Conclusion</h3>
          <p>Memory is about mission. We remember not just to retain—but to rehearse and respond. Jesus told His disciples, "If you abide in my word, you are truly my disciples" (John 8:31). A forgetful church will be a powerless one. But a remembering church—a meditating, rehearsing, applying church—will stand resilient in any generation.</p>
        </div>
      `,
      author: 'Sean Talluri',
      date: '2025-06-05',
      featured_image: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?q=80&w=800&h=450&auto=format&fit=crop',
      categories: ['Scripture', 'Memory', 'Discipleship'],
      tags: ['Deuteronomy 6', 'Bible Memory', 'Spiritual Growth'],
      slug: 'remembering-gods-word-deuteronomy-6'
    },
    {
      id: 2,
      title: 'Prayer as a Community Anchor',
      excerpt: 'Prayer wasn\'t an afterthought in the early church—it was the heartbeat of communal life. Discover how corporate prayer strengthens unity, faith, and spiritual resilience in Christian community.',
      content: `
        <div class="prose max-w-none">
          <p>Prayer wasn't an afterthought in the early Church—it was the heartbeat of communal life. After His ascension, Jesus' disciples "all joined together constantly in prayer" (Acts 1:14, NIV). Later, Paul exhorts believers to "pray continually, give thanks in all circumstances" (1 Thess. 5:16–18).</p>
          
          <p>Prayer is consistently described in Scripture as both a lifestyle and a powerful community practice that roots us in unity, dependence on God, and spiritual resilience. In a fragmented world, the Church's walk in prayer becomes a visible declaration of dependence, compassion, and collective faith.</p>
          
          <h3>Biblical Foundations</h3>
          <p>In Acts 1:14, Luke recounts the leadership's steadfast prayer in the upper room while awaiting the Spirit's arrival. The Greek <em>homothumadon</em>—"with one accord"—implies shared intention and spiritual unity. Paul's instruction in 1 Thessalonians 5:16–18 portrays prayer as a continuous attitude, not just a religious obligation.</p>
          
          <p>Scriptures further highlight that when believers gather in prayer, Christ is present: "For where two or three are gathered in my name, there am I among them" (Matt. 18:20). This is more than poetic encouragement—it is a theological anchor of corporate prayer's authority and presence.</p>
          
          <h3>Why Communal Prayer Matters</h3>
          <ol>
            <li><strong>God's Presence Becomes Tangible:</strong> As a church prays together, spiritual direction and manifestation grow—citing Acts 13:2–3, where "while they were worshiping and fasting," the Spirit gave mission instruction.</li>
            <li><strong>Faith Deepens Across the Community:</strong> Christians attending prayer meetings weekly show increased spiritual health. Prayer builds communal faith and expectation for spiritual movement.</li>
            <li><strong>Emotional and Mental Health Benefits:</strong> Research shows spiritual community involvement correlates with reduced stress and anxiety.</li>
            <li><strong>Reduces Isolation and Fosters Support:</strong> Community prayer connects people in vulnerability, reminding us "we do not bear our crosses alone."</li>
          </ol>
          
          <h3>Practical Strategies</h3>
          <p>Different contexts call for different prayer approaches:</p>
          <ul>
            <li><strong>Sunday Service:</strong> Rotate prayer teams who pray after each sermon segment</li>
            <li><strong>Midweek Gatherings:</strong> Use "response prayer": worship song, then corporate petition</li>
            <li><strong>Small Groups:</strong> Include prayer updates, testimony, thanksgivings</li>
            <li><strong>Online Groups:</strong> Use digital platforms to invite synchronous prayer</li>
          </ul>
          
          <h3>Conclusion</h3>
          <p>Communal prayer is the spiritual glue of the Church—uniting hearts, inviting God's presence, and building faith. As Acts 1 reminds us, steadfast, unified prayer birthed Pentecost. May our churches form unbroken rhythms of prayer, anchoring their identity and mission in shared communion with God.</p>
        </div>
      `,
      author: 'Sean Talluri',
      date: '2025-07-05',
      featured_image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?q=80&w=800&h=450&auto=format&fit=crop',
      categories: ['Prayer', 'Community', 'Church Life'],
      tags: ['Acts 1:14', '1 Thessalonians 5:16-18', 'Corporate Prayer'],
      slug: 'prayer-community-anchor'
    },
    {
      id: 3,
      title: 'Grace and Salvation – Understanding God\'s Gift',
      excerpt: 'Grace and salvation are the heartbeat of the gospel. Understand how God\'s unmerited favor transforms our relationship with Him and reshapes how we live.',
      content: `
        <div class="prose max-w-none">
          <p>Grace and salvation are the heartbeat of the gospel. Paul writes in Ephesians 2:8–9, "For by grace you have been saved through faith—and this is not from yourselves, it is the gift of God—not by works, so that no one can boast." Paul echoes this in Romans 6:23, affirming that "the wages of sin is death, but the gift of God is eternal life in Christ Jesus our Lord."</p>
          
          <p>These twin verses capture the profound biblical truth that our salvation is entirely the work of God, not human effort. This truth challenges the self-reliant impulse that pervades modern culture but offers unparalleled spiritual hope.</p>
          
          <h3>Biblical Foundations and Narrative Arc</h3>
          <p>Grace is woven into Scripture from Genesis to Revelation. Genesis 3:15 foretells a Redeemer who will crush sin—not through human goodness but divine initiative. Abraham's calling is established "by faith" in grace (Gen. 15:6), while the Levitical system reveals sin's cost (Lev. 17:11), culminating in Jesus' atoning death (Heb. 9:12–14).</p>
          
          <p>Paul, the apostle to the Gentiles, frames eternity in brief: sin demands death, but grace gives life. Grace is not cheap—it delivers undeserving sinners from condemnation and empowers a new life in Christ.</p>
          
          <h3>Faith's Dynamic Role</h3>
          <p>If salvation is God's gift, how do we receive it? Through faith—a reliance not on our merit, but on Christ's sufficient work. Ephesians 2:8 underscores that we are saved through faith (Greek <em>dia pisteōs</em>), not because of it. Faith isn't a work; it's the means of receiving grace—like opening a gift, not earning it.</p>
          
          <h3>Theological Implications</h3>
          <ol>
            <li><strong>Assurance of Identity:</strong> We belong to God, not by performance, but by divine initiative. Our value comes from being beloved, not accomplished.</li>
            <li><strong>Freedom to Fail Gracefully:</strong> When grace is understood, failure isn't fatal—it's material for growth.</li>
            <li><strong>Spiritual Maturity:</strong> God equips believers with "every good work" (Eph. 2:10).</li>
            <li><strong>Motivation for Mission:</strong> Grace compels gratitude and service.</li>
          </ol>
          
          <h3>Practical Application</h3>
          <ul>
            <li>Receive daily grace through Scripture, prayer, and confession (1 John 1:9)</li>
            <li>Extend grace to others—especially in mistakes and relational conflict</li>
            <li>Live Gospel-centered lives—serving without expecting reciprocation (Matt. 10:8)</li>
            <li>Create grace-centered small groups that build honest community</li>
          </ul>
          
          <h3>Conclusion</h3>
          <p>Grace is the greatest gospel discovery—a divine gift wrapped in love and power. It reshapes how we see God, ourselves, and the world. When we glimpse grace, we walk in freedom, purpose, and mission. May grace not be merely doctrine, but our daily breath and action.</p>
        </div>
      `,
      author: 'Sean Talluri',
      date: '2025-08-05',
      featured_image: 'https://images.unsplash.com/photo-1507692049790-de58290a4334?q=80&w=800&h=450&auto=format&fit=crop',
      categories: ['Grace', 'Salvation', 'Gospel'],
      tags: ['Ephesians 2:8-9', 'Romans 6:23', 'Salvation'],
      slug: 'grace-salvation-gods-gift'
    },
    {
      id: 4,
      title: 'Faith in Trials — Overcoming Challenges with God\'s Strength',
      excerpt: 'Life\'s journey is filled with unexpected trials, yet Scripture reframes these challenges as pathways to spiritual maturity. Discover how faith grows stronger through adversity.',
      content: `
        <div class="prose max-w-none">
          <p>Life's journey is filled with unexpected trials—loss, illness, rejection, financial struggle, and betrayal can shake us to our core. Yet Scripture reframes these challenges not as dead ends but pathways to spiritual maturity and resilience.</p>
          
          <p>James 1:2–4 urges believers to "consider it pure joy… when you face trials… because the testing of your faith produces perseverance." Similarly, in Romans 5:3–5, Paul points out that suffering leads to perseverance, character, and hope.</p>
          
          <h3>Biblical Framework</h3>
          <p>James 1:2–4 uses the metaphor of endurance refining faith into maturity. "Perseverance must finish its work so that you may be mature and complete, lacking nothing." The Greek term <em>hypomone</em> (perseverance or endurance) originally described athletes and soldiers—implying active, sustained resilience.</p>
          
          <p>Romans 5:3–5 presents a transformative chain: suffering → perseverance → character → hope—not conditioned on our strength, but God's Spirit poured into our hearts.</p>
          
          <h3>Contemporary Reflections</h3>
          <p>Trials, painful as they may be, serve to strengthen and refine our faith. The hardships we experience will result in something more beautiful than we could ever imagine. This sentiment echoes Joseph's life: betrayal, false accusation, and prison paved the way for his rise to leadership and the rescue of many (Gen. 45:7–8).</p>
          
          <h3>Spiritual Practices During Trials</h3>
          <p>To cultivate faith during trials:</p>
          <ul>
            <li><strong>Biblical Reflection:</strong> Meditate on promises like "God is with you…" (Deut. 31:6)</li>
            <li><strong>Scripture Journaling:</strong> Record trials, scriptures, prayers, and insights</li>
            <li><strong>Prayer and Lament:</strong> Psalms offer rich models of honest expression</li>
            <li><strong>Community Sharing:</strong> Share with trusted believers to break isolation</li>
            <li><strong>Service Amid Struggle:</strong> Serving others provides perspective and strengthens faith</li>
          </ul>
          
          <h3>The Apostle Paul's Example</h3>
          <p>Paul endured persecution—floggings, shipwrecks, imprisonment—and yet declared his joy and purpose. In 2 Corinthians 12:9-10, he declares, "My grace is sufficient… for when I am weak, then I am strong." Paul's choice to rejoice in weakness reframes suffering as an opportunity for God's power to shine.</p>
          
          <h3>Conclusion</h3>
          <p>Trials are not anomalies—they are intentional fields where faith flourishes. Scripture holds trials as catalysts for strength, hope, and testimony. Though suffering stings, it primes us for deeper communion with Christ and equips us for ministry. Let us not run from hardship but enter it with expectation—expecting that God will produce perseverance, character, and hope in us.</p>
        </div>
      `,
      author: 'Sean Talluri',
      date: '2025-09-05',
      featured_image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?q=80&w=800&h=450&auto=format&fit=crop',
      categories: ['Faith', 'Trials', 'Perseverance'],
      tags: ['James 1:2-4', 'Romans 5:3-5', '1 Peter 1:6-7'],
      slug: 'faith-in-trials-gods-strength'
    },
    {
      id: 5,
      title: 'Christ at Work – Demonstrating Faith in the Marketplace',
      excerpt: 'Most believers spend the majority of their waking hours at work, yet often compartmentalize spirituality. Learn how to authentically live out Christ in your workplace through excellence, integrity, and witness.',
      content: `
        <div class="prose max-w-none">
          <p>Most believers spend the majority of their waking hours at work, yet often compartmentalize spirituality—leaving faith at the door when crossing into their workspace. This separation contrasts sharply with the biblical vision of work.</p>
          
          <p>From Genesis 2:15, where God commissions Adam to "work it and keep it," to Colossians 3:23–24, which instructs, "whatever you do, work heartily, as for the Lord and not for men… you serve the Lord Christ," Scripture affirms that our work is intrinsically spiritual.</p>
          
          <h3>Biblical Foundations</h3>
          <ul>
            <li><strong>Creation and Vocation:</strong> Before sin marred the world, God declared creation wholly good (Gen. 1:31), then placed Adam in Eden—a gardener and steward of flourishing life (Gen. 2:15).</li>
            <li><strong>Old Testament Leaders:</strong> Joseph's godly leadership in Potiphar's house, and Daniel's faithful prayers under exile illustrate spiritual influence through secular roles.</li>
            <li><strong>New Testament Commission:</strong> Colossians 3:23–24 reframes labor as divine service, not merely human effort.</li>
          </ul>
          
          <h3>Theological Framework</h3>
          <p>Work becomes worship when done unto the Lord. The Cultural Mandate (Gen. 1:28) calls us to cultivate and steward creation as God's ambassadors, while the Great Commission (Matt. 28:18–20) expands this call to disciple nations—including in our workplaces.</p>
          
          <h3>Practical Strategies for Marketplace Discipleship</h3>
          <ol>
            <li><strong>Excellence:</strong> Colossians 3:23 frames work quality as spiritual worship</li>
            <li><strong>Ethical Consistency:</strong> Practice integrity and servant leadership</li>
            <li><strong>Intentional Witness:</strong> Live graciously and speak the gospel when prompted</li>
            <li><strong>Community Engagement:</strong> Model peace and exemplify Christ in everyday interactions</li>
            <li><strong>Spiritual Support Networks:</strong> Join faith groups for mentoring and accountability</li>
          </ol>
          
          <h3>Benefits of Faith at Work</h3>
          <p>Research shows that purpose-driven employees have 1.7x higher job satisfaction and 41% lower burnout. Companies led by faith-based executives see stronger client trust and ethical culture.</p>
          
          <h3>Conclusion</h3>
          <p>God's greatest gift to the world isn't just the gospel—it's redeemed stewards living it out where they spend most of their time. When believers treat work as sacred, it becomes a platform for mission and ministry. Let our workplace lives shine as a "city on a hill" in the marketplace.</p>
        </div>
      `,
      author: 'Sean Talluri',
      date: '2025-10-05',
      featured_image: 'https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?q=80&w=800&h=450&auto=format&fit=crop',
      categories: ['Faith at Work', 'Marketplace Ministry', 'Witness'],
      tags: ['Colossians 3:23-24', 'Genesis 2:15', '1 Peter 2:12'],
      slug: 'christ-at-work-marketplace-faith'
    },
    {
      id: 6,
      title: 'Living Out Scripture – Integrating the Word into Everyday Discipleship',
      excerpt: 'The Bible isn\'t meant to be admired from a distance—it\'s to shape us from the inside out. Learn practical ways to embed Scripture into everyday discipleship through intentional habits and community.',
      content: `
        <div class="prose max-w-none">
          <p>The Bible isn't meant to be admired from a distance—it's to shape us from the inside out. Psalm 119:11 declares, "I have stored up your word in my heart, that I might not sin against you." Colossians 3:16 urges believers to let the word of Christ dwell in you richly, teaching and admonishing one another.</p>
          
          <p>These foundational truths highlight Scripture's purpose—not just for knowledge, but transformation. But how do we move from reading to living?</p>
          
          <h3>Theological Foundation</h3>
          <p>In Psalm 119, the longest psalm and celebration of God's Word, the psalmist repeatedly emphasizes delighting, meditating, and following Scripture. These are not one-time acts but ongoing spiritual rhythms. Colossians 3:16 extends this to community, urging Christ's Word to dwell richly among believers.</p>
          
          <p>2 Timothy 3:16–17 places the authority of Scripture on par with Christ Himself, equipping every believer for spiritual maturity. John Stott writes, "Scripture's purpose is not only to inform our heads, but to transform our lives."</p>
          
          <h3>Biblical Memory and Retrievability</h3>
          <p>Psalm 119:11's claim—hiding God's Word in the heart—marks memorization as spiritual warfare and identity formation. Recall how Jesus wielded Scripture in spiritual warfare (Matt. 4:4, 7, 10). When we store Scripture, we train ourselves to think biblically.</p>
          
          <h3>Practical Model for Daily Integration</h3>
          <ol>
            <li><strong>Morning Memory Moment:</strong> Begin with a Scripture verse—read, speak out loud, reflect for 2 minutes</li>
            <li><strong>Journaling Prompts:</strong> At lunch or reflection times, journal how the verse shaped decisions</li>
            <li><strong>Conversation Check-ins:</strong> In small groups, share weekly memory verses and application</li>
            <li><strong>Evening Review Routine:</strong> Close the day with light repetition</li>
            <li><strong>Family Engagement:</strong> Practice Scripture with children using songs and dialogue</li>
            <li><strong>Workplace Reminders:</strong> Place verses in visible areas to pause and pray</li>
          </ol>
          
          <h3>Overcoming Barriers</h3>
          <ul>
            <li><strong>Time Constraints:</strong> Begin with one verse/week using 3-minute "micro habits"</li>
            <li><strong>Frustration or Forgetting:</strong> Use multisensory cues: index cards, voice recordings</li>
            <li><strong>Lack of Community:</strong> Form memory partners or join memory challenges</li>
            <li><strong>Digital Fatigue:</strong> Alternate between app reminders and paper notes</li>
          </ul>
          
          <h3>Conclusion</h3>
          <p>Living Scripture isn't passive—it's activation of God's Word in personal, communal, and vocational spheres. When Scripture is central, discipleship becomes multidimensional: worship shapes work, prayer solidifies convictions, and conversation encourages reflection. May your heart be a garden of hidden verses, yielding truth, grace, and transformation wherever God places you.</p>
        </div>
      `,
      author: 'Sean Talluri',
      date: '2025-11-05',
      featured_image: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?q=80&w=800&h=450&auto=format&fit=crop',
      categories: ['Scripture', 'Discipleship', 'Christian Living'],
      tags: ['Psalm 119:11', 'Colossians 3:16', '2 Timothy 3:16-17'],
      slug: 'living-out-scripture-everyday-discipleship'
    }
  ];

  const currentPost = blogPosts.find(post => post.slug === slug);

  if (!currentPost) {
    return (
      <div className="pt-16 lg:pt-20 pb-20 lg:pb-0 min-h-screen">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-800 mb-4 font-display">Post Not Found</h1>
            <p className="text-gray-600 mb-8 font-sans">The blog post you're looking for doesn't exist.</p>
            <button
              onClick={() => navigate('/blog')}
              className="bg-[#f17b21] hover:bg-[#d86919] text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans"
            >
              Back to Blog
            </button>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="pt-16 lg:pt-20 pb-20 lg:pb-0 min-h-screen">
      {/* Header */}
      <section className="py-8 lg:py-12 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4">
          <button
            onClick={() => navigate('/blog')}
            className="flex items-center space-x-2 text-[#f17b21] hover:text-[#d86919] font-semibold transition-colors duration-300 font-sans mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to All Posts</span>
          </button>
          
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <div className="flex flex-wrap gap-2 mb-4">
                {currentPost.categories.map((category, index) => (
                  <span key={index} className="bg-[#f17b21] text-white px-3 py-1 rounded-full text-sm font-semibold font-sans">
                    {category}
                  </span>
                ))}
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-6 font-display leading-tight">
                {currentPost.title}
              </h1>
              
              <div className="flex items-center space-x-6 text-gray-500 font-sans">
                <div className="flex items-center space-x-2">
                  <User className="w-5 h-5" />
                  <span>By {currentPost.author}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="w-5 h-5" />
                  <span>{formatDate(currentPost.date)}</span>
                </div>
              </div>
            </div>

            {/* Featured Image */}
            <div className="mb-8 lg:mb-12">
              <img 
                src={currentPost.featured_image}
                alt={currentPost.title}
                className="w-full h-64 lg:h-96 object-cover rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div 
              className="prose prose-lg max-w-none font-sans"
              dangerouslySetInnerHTML={{ __html: currentPost.content }}
            />
            
            {/* Tags */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex items-center space-x-3 mb-4">
                <Tag className="w-5 h-5 text-[#f17b21]" />
                <span className="font-semibold text-gray-800 font-sans">Tags:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {currentPost.tags.map((tag, index) => (
                  <span key={index} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-sans">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/blog')}
                  className="bg-[#f17b21] hover:bg-[#d86919] text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans"
                >
                  Read More Posts
                </button>
                <button
                  onClick={() => navigate('/subscribe')}
                  className="border-2 border-[#f17b21] text-[#f17b21] hover:bg-[#f17b21] hover:text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans"
                >
                  Subscribe for Updates
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPostPage;